import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, ClipboardText, CheckCircle, Truck } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const ICONS = { docket_submitted: ClipboardText, transport_delivered: Truck };

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setCount(data.count); } catch {}
  }, []);
  const loadItems = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadItems();
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); setItems((xs) => xs.map((x) => ({ ...x, read: true }))); setCount(0); } catch {}
  };

  const clickItem = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch {} }
    setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    loadCount();
    setOpen(false);
    if (n.project_id) navigate(`/?project=${n.project_id}&tab=dockets`);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative text-inherit" data-testid="notifications-button">
        <Bell size={19} weight={count > 0 ? "fill" : "bold"} className={count > 0 ? "text-equipr-yellow" : ""} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#FF4F00] ring-[1.5px] ring-[#121212]" data-testid="notifications-badge" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 z-50 w-80 bg-surface-panel border border-border-warm rounded-[14px] overflow-hidden shadow-modal text-equipr-black" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-2.5 bg-equipr-black text-white">
            <span className="text-sm font-bold">Notifications</span>
            {items.some((x) => !x.read) && (
              <button onClick={markAllRead} data-testid="notifications-mark-all" className="text-[11px] text-equipr-yellow hover:underline">Mark all read</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-400" data-testid="notifications-empty">
                <CheckCircle size={24} className="mx-auto mb-2 text-gray-300" /> You're all caught up
              </div>
            ) : items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button key={n.id} onClick={() => clickItem(n)} data-testid={`notification-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-divider-row hover:bg-surface-panel flex gap-3 ${n.read ? "" : "bg-equipr-yellow/5"}`}>
                  <div className={`mt-0.5 ${n.read ? "text-gray-400" : "text-equipr-black"}`}><Icon size={18} weight="bold" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
                      <span className="text-sm font-semibold truncate">{n.title}</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-snug mt-0.5">{n.message}</p>
                    <span className="text-[10px] text-gray-400">{timeAgo(n.created_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
